/* eslint-disable react/no-unstable-nested-components */
import type { CurrencyCode } from 'hooks/globalApi';
import type { InvoiceStatus } from 'payments-api/model/invoiceStatus';
import {
  useGetInvoicesSuspense,
  useGetProductsSuspense,
  useGetUsersSuspense,
  usePostInvoicesId,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { MainViewLayout } from 'components/Layout/MainViewLayout';
import { useToast } from 'components/ToastManager';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import {
  type Product,
  AddLineItemOperationOp,
  DeleteLineItemOperationOp,
  UpdateLineItemOperationOp,
} from 'payments-api/model';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';

import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import {
  type InvoiceRowItem,
  InvoiceItemTable,
} from '@fragment/ui/src/components/Payments/InvoiceItemTable';
import { LayoutRow } from '@fragment/ui/src/components/Payments/LayoutRow';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { InvoiceItem } from './ItemsPane';
import type { Obligation } from './OverviewPane';
import {
  getPayoutUserConfig,
  getProductSelectionUpdates,
  PLATFORM_PAYOUT_USER,
} from '../invoiceItemUtils';
import { InvoiceLayout } from '../InvoiceLayout';
import { OverviewPane } from './OverviewPane';
import {
  type PendingAdd,
  type PendingChange,
  type PendingRemove,
  type PendingUpdate,
  applyPendingChangeOperation,
} from './pendingChangesReducer';

type Props = {
  workspaceId: string;
  invoiceId: string;
};

type ProductWithId = Product & { id: string };

const formatCurrency = (amount: number) =>
  `$${(amount / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const InvoiceDetailPageContent = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data } = useGetInvoicesSuspense();
  const { data: productsData } = useGetProductsSuspense();
  const { data: usersData } = useGetUsersSuspense();
  const postInvoicesIdMutation = usePostInvoicesId();
  const [, setLocation] = useLocation();
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [showDispute, setShowDispute] = useState(false);
  const [acceptedDispute, setAcceptedDispute] = useState(false);

  const users = usersData.data;
  const rawProducts = productsData.data;
  const products: ProductWithId[] = useMemo(
    () =>
      rawProducts.map((product) => ({
        ...product,
        id: product.code,
      })),
    [rawProducts]
  );

  // Find the specific invoice by id
  const invoice = useMemo(
    () => data.data.find((inv) => inv.id === invoiceId),
    [data, invoiceId]
  );

  useDocumentTitle(invoice ? `Invoice ${invoice.id}` : 'Invoice Detail');

  // Original items from invoice (unchanged)
  const originalItems: InvoiceItem[] = useMemo(() => {
    if (!invoice?.lineItems) return [];
    return invoice.lineItems.map((item) => ({
      id: item.id,
      description: item.description,
      productId: item.product_id,
      payoutUser: item.user_id,
      amount: item.amount,
    }));
  }, [invoice]);

  // Extract base obligations (payin and payout) from invoice
  const baseObligations = useMemo(() => {
    if (!invoice) return { payin: [], payout: [] };

    const payin: Obligation[] = [];
    const payoutMap = new Map<string, { amount: number; paid: number }>();

    // Calculate seller obligations (grouped by user_id)
    if (invoice.lineItems) {
      invoice.lineItems.forEach((item) => {
        const amount = Number(item.amount);
        const party = item.user_id;
        if (item.type === InvoiceLineItemType.payout) {
          const existing = payoutMap.get(party) || {
            amount: 0,
            paid: 0,
          };
          existing.amount += amount;
          payoutMap.set(party, existing);
        } else {
          payin.push({
            id: `payin-${item.id}`,
            name: party,
            type: InvoiceLineItemType.payin,
            amount,
            paid: 0, // Payment tracking not yet implemented
          });
        }
      });
    }

    const payout: Obligation[] = Array.from(payoutMap.entries()).map(
      ([name, obligationData]) => ({
        id: `payout-${name}`,
        name,
        type: InvoiceLineItemType.payout,
        amount: obligationData.amount,
        paid: 0, // Payout tracking not yet implemented
      })
    );

    return { payin, payout };
  }, [invoice]);

  // Check if invoice is disputed
  const isDisputed = useMemo(
    () => invoice?.id.toLowerCase().includes('green') || false,
    [invoice]
  );

  // Generate dispute message from a random seller
  const disputeMessage = useMemo(() => {
    if (!isDisputed || !baseObligations.payout.length || !originalItems.length)
      return null;

    // Pick a random seller (payout party)
    const randomSeller =
      baseObligations.payout[
        Math.floor(Math.random() * baseObligations.payout.length)
      ];

    // Pick a random item
    const randomItem =
      originalItems[Math.floor(Math.random() * originalItems.length)];

    // Generate a price that's different from the actual price
    const actualPrice = Number(randomItem.amount);
    const disputedPrice = actualPrice * (1.1 + Math.random() * 1.5); // 70-120% of actual

    return {
      seller: randomSeller.name,
      message: `Hey, the price for "${
        randomItem.description
      }" is ${formatCurrency(actualPrice)}, it should be ${formatCurrency(
        Math.round(disputedPrice)
      )}`,
      itemId: randomItem.id,
      disputedPrice: Math.round(disputedPrice),
    };
  }, [isDisputed, baseObligations.payout, originalItems]);

  // When adjusting: effective items = server items minus pending removes, plus pending adds; apply pending updates to server item amounts
  const effectiveInvoiceItems: InvoiceRowItem[] = useMemo(() => {
    const removedIds = new Set(
      pendingChanges
        .filter((c): c is PendingRemove => c.type === 'remove')
        .map((c) => c.lineItemId)
    );
    const pendingUpdatesMap = new Map(
      pendingChanges
        .filter((c): c is PendingUpdate => c.type === 'update')
        .map((c) => [c.lineItemId, c.amount])
    );

    const uniqueProducts = new Set();
    const uniqueServerItems = (invoice?.lineItems ?? []).flatMap((item) => {
      if (uniqueProducts.has(item.product_id)) return [];
      uniqueProducts.add(item.product_id);
      return [item];
    });

    const serverItems = uniqueServerItems.filter(
      (item) => !removedIds.has(item.id)
    );
    const mapServerItem = (item: (typeof serverItems)[number]) => {
      const payoutUser = item.user_id;
      const currency = item.currencyCode as CurrencyCode;
      const amountOverride = pendingUpdatesMap.get(item.id);
      const amount = amountOverride
        ? Number(amountOverride)
        : Number(item.amount);
      return {
        id: item.id,
        productCode: item.product_id,
        payoutUser,
        description: item.description,
        amount,
        currency,
        currencyPrecision: currencyPrecisionMap[currency],
        balance: 0,
      };
    };
    const serverDisplayItems = serverItems.map(mapServerItem);
    const pendingAdds = pendingChanges.filter(
      (c): c is PendingAdd => c.type === 'add'
    );
    // New items have balance: undefined so the description field is shown
    const addDisplayItems: InvoiceRowItem[] = pendingAdds.map((pa) => ({
      id: pa.tempId,
      productCode: pa.data.productCode,
      payoutUser: pa.data.payoutUser,
      description: pa.data.description,
      amount: Number(pa.data.amount),
      currency: pa.data.currency,
      currencyPrecision: currencyPrecisionMap[pa.data.currency as CurrencyCode],
      balance: undefined,
    }));
    return [...serverDisplayItems, ...addDisplayItems];
  }, [invoice?.lineItems, pendingChanges]);

  // Calculate total per currency: when adjusting use effective items, else server line items
  const totalAmount = useMemo(() => {
    if (isAdjusting) {
      if (effectiveInvoiceItems.length === 0) return [];
      const totalAmountPerCurrency = effectiveInvoiceItems.reduce(
        (acc, item) => {
          acc[item.currency] = (acc[item.currency] || 0) + Number(item.amount);
          return acc;
        },
        {} as Record<CurrencyCode, number>
      );
      return Object.entries(totalAmountPerCurrency).map(
        ([currency, amount]) => ({
          amount,
          currency: currency as CurrencyCode,
        })
      );
    }

    const payInItems = (invoice?.lineItems ?? []).filter(
      (item) => item.type === InvoiceLineItemType.payin
    );
    if (!payInItems.length) return [];
    const totalAmountPerCurrency = payInItems.reduce((acc, item) => {
      const currency = item.currencyCode as CurrencyCode;
      acc[currency] = (acc[currency] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);
    return Object.entries(totalAmountPerCurrency).map(([currency, amount]) => ({
      amount,
      currency: currency as CurrencyCode,
    }));
  }, [isAdjusting, effectiveInvoiceItems, invoice?.lineItems]);

  // Calculate the dispute delta (price difference)
  const disputeDelta = useMemo(() => {
    if (!disputeMessage) return 0;

    const originalItem = originalItems.find(
      (item) => item.id === disputeMessage.itemId
    );
    if (!originalItem) return 0;

    return disputeMessage.disputedPrice - Number(originalItem.amount);
  }, [disputeMessage, originalItems]);

  // Base obligations with accepted dispute applied (if accepted)
  const adjustedBaseObligations = useMemo(() => {
    if (!acceptedDispute || !disputeMessage || disputeDelta === 0) {
      return baseObligations;
    }

    // Apply dispute delta to all obligations permanently
    const adjustedPayin: Obligation[] = baseObligations.payin.map(
      (obligation) => ({
        ...obligation,
        amount: obligation.amount + disputeDelta,
        paid: obligation.paid + disputeDelta,
      })
    );

    const adjustedPayout: Obligation[] = baseObligations.payout.map(
      (obligation) => ({
        ...obligation,
        amount: obligation.amount + disputeDelta,
      })
    );

    return { payin: adjustedPayin, payout: adjustedPayout };
  }, [baseObligations, acceptedDispute, disputeMessage, disputeDelta]);

  // Obligations to display (modified when showing dispute)
  const obligations = useMemo(() => {
    if (
      !showDispute ||
      !disputeMessage ||
      disputeDelta === 0 ||
      acceptedDispute
    ) {
      return adjustedBaseObligations;
    }

    // Apply dispute delta to all obligations
    const modifiedPayin: Obligation[] = [];
    adjustedBaseObligations.payin.forEach((obligation) => {
      // Add original obligation with strikethrough flag
      modifiedPayin.push({
        ...obligation,
        isStrikethrough: true,
      });
      // Add new obligation with adjusted amount
      modifiedPayin.push({
        ...obligation,
        id: `${obligation.id}-disputed`,
        amount: obligation.amount + disputeDelta,
        paid: obligation.paid + disputeDelta,
        isDisputed: true,
      });
    });

    const modifiedPayout: Obligation[] = [];
    adjustedBaseObligations.payout.forEach((obligation) => {
      // Add original obligation with strikethrough flag
      modifiedPayout.push({
        ...obligation,
        isStrikethrough: true,
      });
      // Add new obligation with adjusted amount
      modifiedPayout.push({
        ...obligation,
        id: `${obligation.id}-disputed`,
        amount: obligation.amount + disputeDelta,
        isDisputed: true,
      });
    });

    return { payin: modifiedPayin, payout: modifiedPayout };
  }, [
    adjustedBaseObligations,
    showDispute,
    disputeMessage,
    disputeDelta,
    acceptedDispute,
  ]);

  const handleBackToInvoices = () => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
    );
  };

  const handleObligationClick = (obligation: Obligation) => {
    // Navigate to user detail page
    setLocation(
      makePath(['w', workspaceId, 'payments', 'users', obligation.name], {
        absolute: true,
      })
    );
  };

  const handleAddBlankItem = useCallback(() => {
    const tempId = Date.now().toString();
    const blankItem = {
      id: '',
      productCode: '',
      payoutUser: '',
      description: '',
      amount: 0,
      currency: 'USD' as const,
      currencyPrecision: 2,
      balance: 0,
    };
    setPendingChanges((prev) =>
      applyPendingChangeOperation(prev, { op: 'add', tempId, data: blankItem })
    );
  }, []);

  const handleRemoveItem = useCallback((id: string) => {
    setPendingChanges((prev) =>
      applyPendingChangeOperation(prev, { op: 'remove', id })
    );
  }, []);

  const handleUpdateItem = useCallback(
    (id: string, field: string, value: string | number) => {
      setPendingChanges((prev) => {
        let next = applyPendingChangeOperation(prev, {
          op: 'update',
          id,
          field,
          value,
        });

        // When product is selected, auto-set payout party based on product type
        if (field === 'productCode' && typeof value === 'string') {
          const productUpdates = getProductSelectionUpdates(value, rawProducts);
          if (productUpdates) {
            next = applyPendingChangeOperation(next, {
              op: 'update',
              id,
              field: 'payoutUser',
              value: productUpdates.payoutUser,
            });
          }
        }

        return next;
      });
    },
    [rawProducts]
  );

  const handleSubmitChanges = useCallback(async () => {
    if (!invoiceId || pendingChanges.length === 0) return;
    const removes = pendingChanges.filter(
      (c): c is PendingRemove => c.type === 'remove'
    );
    const adds = pendingChanges.filter(
      (c): c is PendingAdd => c.type === 'add'
    );
    const updates = pendingChanges.filter(
      (c): c is PendingUpdate => c.type === 'update'
    );
    const lineItemsUpdate = [
      ...removes.map((r) => ({
        op: DeleteLineItemOperationOp.delete,
        id: r.lineItemId,
      })),
      ...updates.map((u) => ({
        op: UpdateLineItemOperationOp.update,
        id: u.lineItemId,
        amount: u.amount,
      })),
      ...adds.map((a) => ({
        op: AddLineItemOperationOp.add,
        type: InvoiceLineItemType.payout,
        product_id: a.data.productCode,
        amount: String(a.data.amount),
        currency_code: a.data.currency,
        description: a.data.description,
        user_id:
          a.data.payoutUser === PLATFORM_PAYOUT_USER
            ? ''
            : (a.data.payoutUser as string),
        platform: a.data.payoutUser === PLATFORM_PAYOUT_USER,
      })),
    ];
    try {
      await postInvoicesIdMutation.mutateAsync({
        id: invoiceId,
        data: { line_items_update: lineItemsUpdate },
      });
      await queryClient.invalidateQueries({ queryKey: ['/invoices'] });
      setPendingChanges([]);
      setIsAdjusting(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save changes.';
      showToast(<Toast type="error" message={message} />);
    }
  }, [
    invoiceId,
    pendingChanges,
    postInvoicesIdMutation,
    queryClient,
    showToast,
  ]);

  const handleCancelAdjust = useCallback(() => {
    setPendingChanges([]);
    setIsAdjusting(false);
  }, []);

  const primaryActionTitle = useMemo(() => {
    if (postInvoicesIdMutation.isPending) return 'Wait...';
    if (isAdjusting) return 'Submit';
    return 'Adjust';
  }, [postInvoicesIdMutation.isPending, isAdjusting]);

  // Validate that all new items have required fields filled in
  const canSubmitAdjustment = useMemo(() => {
    if (pendingChanges.length === 0) return false;
    const pendingAdds = pendingChanges.filter(
      (c): c is PendingAdd => c.type === 'add'
    );
    // If there are no adds, allow submission (removes/updates only)
    if (pendingAdds.length === 0) return true;
    // All new items must have productCode, payoutUser, and amount > 0
    return pendingAdds.every(
      (add) =>
        add.data.productCode.trim() !== '' &&
        add.data.payoutUser.trim() !== '' &&
        add.data.amount > 0
    );
  }, [pendingChanges]);

  if (!invoice) {
    return (
      <MainViewLayout hideSearch>
        <div className="text-main-500">Invoice not found</div>
      </MainViewLayout>
    );
  }

  return (
    <>
      <InvoiceLayout
        onBack={handleBackToInvoices}
        title={invoice.id}
        status={invoice.status as InvoiceStatus}
        primaryAction={{
          title: primaryActionTitle,
          'data-testid': 'submit-invoice-button',
          onClick: isAdjusting
            ? handleSubmitChanges
            : () => setIsAdjusting(true),
          disabled: true,
          disabledTooltipText: 'Adjusting invoices is not available yet.',
        }}
        secondaryAction={
          isAdjusting
            ? {
                title: 'Cancel',
                'data-testid': 'cancel-invoice-button',
                onClick: handleCancelAdjust,
              }
            : {
                title: 'History',
                'data-testid': 'history-invoice-button',
                onClick: () => {},
              }
        }
        totalAmount={totalAmount}
        issued={invoice.created}
        buyer={undefined}
      >
        <LayoutRow height="fit" justify="start">
          <InvoiceItemTable
            invoiceItems={effectiveInvoiceItems}
            partialEditItemIds={invoice.lineItems?.map((item) => item.id) ?? []}
            onAddInvoiceItem={handleAddBlankItem}
            removeInvoiceItem={(tempId: string) => {
              handleRemoveItem(tempId);
            }}
            setPrice={(itemId: string, amount: number) =>
              handleUpdateItem(itemId, 'amount', amount)
            }
            setCurrency={(itemId, currency) =>
              handleUpdateItem(itemId, 'currency', currency)
            }
            showBalance={!isAdjusting}
            setDescription={(itemId: string, description: string) =>
              handleUpdateItem(itemId, 'description', description)
            }
            setPayoutUser={(itemId: string, payoutUser: string) =>
              handleUpdateItem(itemId, 'payoutUser', payoutUser)
            }
            setProductCode={(itemId: string, productCode: string) =>
              handleUpdateItem(itemId, 'productCode', productCode)
            }
            productOptions={products.map((product) => ({
              value: product.code,
              label: product.code,
            }))}
            getPayoutUserOptions={(item) =>
              getPayoutUserConfig(
                item.productCode || undefined,
                rawProducts,
                users
              ).options
            }
          />
        </LayoutRow>
        <OverviewPane
          obligations={obligations}
          onObligationClick={handleObligationClick}
          isAdjusting={isAdjusting}
        />
      </InvoiceLayout>
    </>
  );
};

export const InvoiceDetailPage = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => (
  <Suspense
    fallback={
      <MainViewLayout hideSearch>
        <div className="text-main-500">Loading invoice...</div>
      </MainViewLayout>
    }
  >
    <InvoiceDetailPageContent workspaceId={workspaceId} invoiceId={invoiceId} />
  </Suspense>
);
